'use es6';

import ErrorBoundary from 'customer-data-objects-ui-components/ErrorBoundary';
import SidebarError from '../SidebarError';
import { useCallback, useState, useEffect } from 'react';
import devLogger from 'react-utils/devLogger';
import PropTypes from 'prop-types';
import UniversalSidebarCardsContainer from './UniversalSidebarCardsContainer';
import UniversalSidebarPreviewPanel from './UniversalSidebarPreviewPanel';
import { SidebarPropertySourceContext } from './context/SidebarPropertySourceContext';
import { IsEmbeddedContext } from './context/IsEmbeddedContext';
import { SourceAppContext } from './context/SourceAppContext';
import { HostAppCallbacksContext } from './context/HostAppCallbacksContext';
import { CardLocationContext } from './context/CardLocationContext';
import { ObjectTypeFromIds } from 'customer-data-objects/constants/ObjectTypeIds';
import get from 'transmute/get';
import { ApolloProvider, ApolloClient } from '@apollo/client';
import { client } from 'customer-data-sidebar/graphQL/client';
import SharedDragDropContext from 'customer-data-ui-utilities/dnd/SharedDragDropContext';
import getDragDropContextWrapper from './getDragDropContextWrapper';
import emptyFunction from 'react-utils/emptyFunction';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import { INLINE, PANEL, UniversalSidebarUsePropType } from 'customer-data-sidebar/universal/constants/UniversalSidebarUseConstants';
import { QUOTE } from 'customer-data-objects/constants/ObjectTypes';
import MessageBusProvider from 'crm-message-bus/MessageBusProvider';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { CrmMessagingContext } from 'crm-message-bus/CrmMessagingContext';
import { CrmLinksDataProvider } from 'crm-links/common/context/CrmLinksDataProvider';
import { Metrics } from '../Metrics';
import Raven from 'raven-js';
import { PropertiesProvider } from 'customer-data-properties/context';
import LegacyPreviewContextProvider from './LegacyPreviewContextProvider';
import { normalizeTypeId, denormalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';

// TODO: In an effort to scale better™ we'd eventually like to move this capability to the schema metadata service once it's available
// through GraphQL. Context: https://hubspot.slack.com/archives/C017SPT9U5P/p1611243088010600
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const READONLY_SIDEBAR_OBJECTS = [QUOTE];
export const logPreviewSidebarObject = objectTypeId => {
  CrmLogger.log('previewSidebarObject', {
    action: 'preview record',
    objectTypeId
  });
};
function getObjectTypeForMetrics(objectType) {
  // transform the object type to category-typeId format (ex: 0-1)
  const normalizedObjectType = normalizeTypeId(objectType);
  if (normalizedObjectType.startsWith('0-')) {
    // Return the denormalized object type (ex: CONTACT, COMPANY, etc.)
    return denormalizeTypeId(normalizedObjectType);
  }
  return 'OTHER';
}
const UniversalSidebar = ({
  apolloClient = client,
  auth,
  cardComponents = {},
  cardLocation,
  cardProps,
  DragDropContext = SharedDragDropContext,
  HighlightComponent,
  isEmbedded = false,
  objectType,
  onClose = emptyFunction,
  onAssociationUpdate = emptyFunction,
  onObjectCreate = emptyFunction,
  onObjectDelete = emptyFunction,
  onObjectUpdate = emptyFunction,
  previewCardLocation = cardLocation,
  PreviewHighlightComponent = HighlightComponent,
  readOnly = false,
  sourceApp,
  sourceId,
  subjectId,
  use = INLINE,
  RhumbMarkerSuccess,
  RhumbMarkerError
}) => {
  const [previewConfig, setPreviewConfig] = useState(null);
  const previewObjectId = get('objectId', previewConfig);
  const previewObjectTypeId = get('objectTypeId', previewConfig);
  const previewObjectType = ObjectTypeFromIds[previewObjectTypeId] || previewObjectTypeId;
  useEffect(() => {
    if (objectType && subjectId) {
      Metrics.counter('sidebar-rendered', {
        location: cardLocation,
        app: sourceApp,
        objectType: getObjectTypeForMetrics(objectType)
      }).increment();
    }
  }, [cardLocation, sourceApp, objectType, subjectId]);
  useEffect(() => {
    if (previewObjectId && previewObjectTypeId) {
      Metrics.counter('nested-preview-rendered', {
        location: previewCardLocation,
        app: sourceApp,
        objectType: getObjectTypeForMetrics(previewObjectTypeId)
      }).increment();
    }
  }, [previewObjectId, previewObjectTypeId, previewCardLocation, sourceApp]);
  const closeSidebar = useCallback(() => {
    onClose();
    setPreviewConfig(null);
  }, [onClose, setPreviewConfig]);
  useOnCrmMessageTopic(TOPIC_NAMES.CLOSE_PREVIEW_OBJECT, closeSidebar);
  useEffect(() => {
    if (!sourceApp) {
      devLogger.warn({
        message: `No source app provided for the universal sidebar. Please pass 'sourceApp={YOUR_APP}'.`,
        key: `UniversalSidebar::render`
      });
      Raven.captureException(new Error(`No source app provided for the universal sidebar. Please pass 'sourceApp={YOUR_APP}''.`), {
        extra: {
          location: cardLocation
        }
      });
    }
  }, [sourceApp, cardLocation]);
  if (!subjectId || !objectType || !auth) {
    return null;
  }
  const UniversalDragDropContextWrapper = getDragDropContextWrapper(DragDropContext);
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    ErrorComponent: SidebarError,
    boundaryName: "UniversalSidebar_SidebarError",
    showRefreshAlert: true,
    children: /*#__PURE__*/_jsx(ApolloProvider, {
      client: apolloClient,
      children: /*#__PURE__*/_jsx(CrmLinksDataProvider, {
        apolloClient: apolloClient,
        children: /*#__PURE__*/_jsx(PropertiesProvider, {
          children: /*#__PURE__*/_jsx(UniversalDragDropContextWrapper, {
            DragDropContext: DragDropContext,
            children: /*#__PURE__*/_jsx(IsEmbeddedContext.Provider, {
              value: !!isEmbedded,
              children: /*#__PURE__*/_jsx(SidebarPropertySourceContext.Provider, {
                value: {
                  propertySource: sourceApp,
                  propertySourceId: sourceId
                },
                children: /*#__PURE__*/_jsx(SourceAppContext.Provider, {
                  value: {
                    sourceApp
                  },
                  children: /*#__PURE__*/_jsx(MessageBusProvider, {
                    children: /*#__PURE__*/_jsx(LegacyPreviewContextProvider, {
                      setPreviewConfig: setPreviewConfig,
                      logPreviewSidebarObject: logPreviewSidebarObject,
                      subjectId: subjectId,
                      objectType: objectType,
                      closeSidebar: closeSidebar,
                      shouldOpenOnRender: use === PANEL,
                      children: /*#__PURE__*/_jsx(HostAppCallbacksContext.Provider, {
                        value: {
                          onAssociationUpdate,
                          onObjectCreate,
                          onObjectDelete,
                          onObjectUpdate
                        },
                        children: /*#__PURE__*/_jsx(CrmMessagingContext.Provider, {
                          value: {
                            messagingId: cardLocation
                          },
                          children: /*#__PURE__*/_jsxs(CardLocationContext.Provider, {
                            value: {
                              cardLocation
                            },
                            children: [previewObjectId && previewObjectType && /*#__PURE__*/_jsx(CrmMessagingContext.Provider, {
                              value: {
                                messagingId: `preview-${previewCardLocation}`
                              },
                              children: /*#__PURE__*/_jsx(CardLocationContext.Provider, {
                                value: {
                                  cardLocation: previewCardLocation,
                                  parentCardLocation: cardLocation
                                },
                                children: /*#__PURE__*/_jsx(UniversalSidebarPreviewPanel, {
                                  use: use,
                                  children: /*#__PURE__*/_jsx(UniversalSidebarCardsContainer, {
                                    auth: auth,
                                    cardComponents: cardComponents,
                                    cardProps: cardProps,
                                    HighlightComponent: PreviewHighlightComponent,
                                    objectType: previewObjectType,
                                    objectId: previewObjectId,
                                    onClose: closeSidebar,
                                    readOnly: readOnly || READONLY_SIDEBAR_OBJECTS.includes(previewObjectType),
                                    use: PANEL
                                  })
                                })
                              })
                            }), use !== PANEL ? /*#__PURE__*/_jsx(UniversalSidebarCardsContainer, {
                              auth: auth,
                              cardComponents: cardComponents,
                              cardProps: cardProps,
                              HighlightComponent: HighlightComponent,
                              objectType: objectType,
                              objectId: subjectId,
                              onClose: onClose,
                              readOnly: readOnly || READONLY_SIDEBAR_OBJECTS.includes(objectType),
                              RhumbMarkerSuccess: RhumbMarkerSuccess,
                              RhumbMarkerError: RhumbMarkerError,
                              use: use
                            }) : null]
                          })
                        })
                      })
                    })
                  })
                })
              })
            })
          })
        })
      })
    })
  });
};
UniversalSidebar.propTypes = {
  AddObjectComponent: PropTypes.elementType,
  apolloClient: PropTypes.instanceOf(ApolloClient),
  auth: PropTypes.object.isRequired,
  cardComponents: PropTypes.object,
  cardLocation: PropTypes.string.isRequired,
  cardProps: PropTypes.object,
  DragDropContext: PropTypes.func,
  HighlightComponent: PropTypes.elementType,
  isEmbedded: PropTypes.bool,
  objectType: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onAssociationUpdate: PropTypes.func,
  onObjectCreate: PropTypes.func,
  onObjectDelete: PropTypes.func,
  onObjectUpdate: PropTypes.func,
  previewCardLocation: PropTypes.string,
  PreviewHighlightComponent: PropTypes.elementType,
  readOnly: PropTypes.bool,
  sourceApp: PropTypes.string,
  sourceId: PropTypes.string,
  subjectId: PropTypes.string.isRequired,
  use: UniversalSidebarUsePropType,
  RhumbMarkerSuccess: PropTypes.node,
  RhumbMarkerError: PropTypes.node
};
export default UniversalSidebar;